import { useCallback } from 'react';
import type { BlockType } from '@unifyapps/defs/types/block';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import { useGetGlobalStateStoreState } from '../../../stores/GlobalStateStore';
import { getBlocksFromPage, useGetInterfaceStoreState } from '../../../stores/InterfaceStore';
import { useDataSourceRecordStore } from '../../../stores/DataSourceRecordStore';
import { getIsDataSourceAnEntityType } from '../../../utils/entityDataSource';
import { getEntityTypeDataSourceFromBlock } from '../../../components/DataSourceController/utils/getEntityTypeDataSourceFromBlock';

type MiddlewareParams = {
  pageBlocks?: Record<string, BlockType | undefined>;
};

// Because entity data sources are created on the fly,
// they have their references stored in the block.datasourceIds.
// However, they don't have datasource records stored,
// and we create them on the fly, so we are iterating over all the blocks to find the entity data sources
function getEntityDataSource(middlewareParams: MiddlewareParams) {
  const { pageBlocks } = middlewareParams;
  const entityDataSources = Object.values(pageBlocks ?? {}).reduce<
    Record<string, DataSourceEntity | undefined>
  >((acc, block) => {
    if (!block) return acc;
    const dataSourceIds = block.dataSourceIds ?? [];
    dataSourceIds.forEach((id) => {
      if (getIsDataSourceAnEntityType(id)) {
        // passing fake activeInterfacePageId because it doesn't matter
        acc[id] = getEntityTypeDataSourceFromBlock(block, 'fakeActiveInterfacePageId');
      }
    });
    return acc;
  }, {});

  return entityDataSources;
}

export function useBuildEntitiesForDependencies() {
  const { getGlobalStateStoreState } = useGetGlobalStateStoreState();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  const getDataSourceRecordStoreState = useDataSourceRecordStore().getState;

  // need to use get method of store because we want the latest values of the store here, not using that
  // makes this function a render cycle back and inconsistent dependencies
  const getEntitiesForDependencies = useCallback(() => {
    const globalStateStoreState = getGlobalStateStoreState();
    const interfaceStoreState = getInterfaceStoreState();
    const dataSourceRecords = getDataSourceRecordStoreState().dataSources;
    const activeInterfacePage =
      interfaceStoreState.interfacePages[interfaceStoreState.activeInterfacePageId];
    const pageFunctions = activeInterfacePage.properties.pageFunctions;
    const stateBlocks = globalStateStoreState.blocks;

    const pageBlocks = getBlocksFromPage(
      globalStateStoreState.deviceDetails.device,
      interfaceStoreState,
    );

    const entityDataSources = getEntityDataSource({ pageBlocks });

    const groupedEntityList = {
      // NOTE: state blocks also have runtimeBlockIds
      blocks: { ...stateBlocks },
      dataSources: { ...dataSourceRecords },
      pageFunctions: { ...pageFunctions },
      entityDataSources,
    };

    return { groupedEntityList };
  }, [getDataSourceRecordStoreState, getGlobalStateStoreState, getInterfaceStoreState]);

  return { getEntitiesForDependencies };
}

export type EntityListType = ReturnType<
  ReturnType<typeof useBuildEntitiesForDependencies>['getEntitiesForDependencies']
>['groupedEntityList'];
